import type { OpenProjectSessionInput } from '@/services/api/open-project-input';

export interface ResolveVersionPreviewInitialSessionInputOptions {
  /** 路由解析出的 OpenProject 基础上下文。 */
  readonly sessionInput: OpenProjectSessionInput;
  /** URL query 中解析出的首屏提交 ID；优先来自 versionId，其次来自 adoptedCommitId。 */
  readonly initialCommitId: string | null | undefined;
}

/**
 * 解析版本预览首屏应传给 OpenProject 的会话输入。
 *
 * 设计说明：
 * 1. 版本预览 URL 带 `versionId` 时，首屏模型应直接打开该提交，避免先加载采纳版本再二次切换；
 * 2. 没有 `versionId` 但带 `adoptedCommitId` 时，首屏直接打开采纳提交，减少一次模型重载；
 * 3. 没有任何首屏提交 ID 时复用原始输入对象，保持编辑页和预览页共享的默认打开语义；
 * 4. 这里只处理初始化边界，后续用户点击版本仍由 controller 走历史快照切换流程。
 *
 * @param options 版本预览初始会话解析选项。
 * @returns 可直接传给 `initSessionAndLoadFiles` 的 OpenProject 输入。
 */
export function resolveVersionPreviewInitialSessionInput(
  options: ResolveVersionPreviewInitialSessionInputOptions,
): OpenProjectSessionInput {
  const commitId = options.initialCommitId?.trim() ?? '';
  if (!commitId) {
    return options.sessionInput;
  }

  // OpenProject 输入同时保留 bigint 字段和文本字段；提交 ID 只有字符串语义，
  // 因此在这里补齐 commitId 与 commitIdText，避免 URL 首屏和后续路由回写脱节。
  return {
    ...options.sessionInput,
    commitId,
    commitIdText: commitId,
  };
}
